import * as api from "@/api";
import styles from "@/components/feedback-popup.module.css";
import MenuShell from "@/components/menu-shell";
import PopupShell from "@/components/popup-shell";
import { PositionInfo } from "@/types";
import { closePopup, getSupportLink } from "@/utils";
import { faCircleQuestion } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";
import i18next, { t } from "i18next";
import { JSX, MouseEvent, ReactNode, useState } from "react";
import { Trans } from "react-i18next";

interface FeedbackPopupProps {
	isShown: boolean;
}

function FeedbackPopup({ isShown }: FeedbackPopupProps): JSX.Element {
	const [isChannelDetailPopupOpen, setIsChannelDetailPopupOpen] =
		useState<boolean>(false);
	const [isChannelDetailPopupClosing, setIsChannelDetailPopupClosing] =
		useState<boolean>(false);
	const [channelDetailPopupPosition, setChannelDetailPopupPosition] =
		useState<PositionInfo>({
			left: 0,
			top: 0,
		});
	const [channelDetailPopupContent, setChannelDetailPopupContent] =
		useState<ReactNode>();

	const { data, isError } = useQuery({
		queryFn: api.getFaq,
		queryKey: ["faq"],
		refetchOnMount: false,
		refetchOnWindowFocus: false,
	});

	const closeFeedbackPopup = (): void => {
		closePopup("feedback");
	};

	const handleChannelDetailPopupClose = (): void => {
		setIsChannelDetailPopupClosing(true);
		window.setTimeout(() => {
			setIsChannelDetailPopupOpen(false);
			setIsChannelDetailPopupClosing(false);
		}, 350);
	};

	const openChannelDetailPopup = (
		event: MouseEvent<HTMLButtonElement>,
		content: ReactNode,
	): void => {
		const rect = event.currentTarget.getBoundingClientRect();
		setChannelDetailPopupPosition({
			left: rect.left,
			top: rect.top + rect.height + 8,
		});
		setChannelDetailPopupContent(content);
		setIsChannelDetailPopupOpen(true);
	};

	const feedbackChannelList = [
		{
			backgroundColor: "rgba(55, 157, 168, .1)",
			color: "rgb(55, 157, 168)",
			icon: "\ue882",
			name: t("qqGroup"),
			onClick: (event: MouseEvent<HTMLButtonElement>): void => {
				openChannelDetailPopup(
					event,
					<>
						<div>
							QQ 扫一扫或
							<a
								className="link"
								rel="noreferrer"
								target="_blank"
								href="https://qm.qq.com/cgi-bin/qm/qr?_wv=1027&k=ysuGDCHA6SVUK-Glc15Di8KJ4yDgWVKK&authKey=q2mQe5J8jFHwXt0pMbrEQUx9yVdTyp7k2kE%2FN7pqYp0zv6Irzu6z7P4ioHLeadEE&noverify=0&group_code=941653619"
							>
								点击此处
							</a>
							<br />
							加入反馈交流群 941653619
						</div>
						<img
							src="https://limestart-assets.retiehe.com/qrcode-qq-group-3.jpg"
							alt="QQ群二维码"
							width="128"
							height="128"
						/>
					</>,
				);
			},
		},
		{
			backgroundColor: "rgba(106, 168, 55, .1)",
			color: "rgb(106, 168, 55)",
			icon: "\ue884",
			name: t("wechatOfficialAccount"),
			onClick: (event: MouseEvent<HTMLButtonElement>): void => {
				openChannelDetailPopup(
					event,
					<>
						<div>
							微信扫一扫
							<br />
							关注「青柠起始页」公众号
						</div>
						<img
							src="https://limestart-assets.retiehe.com/qrcode-wechat-official-account.jpeg"
							alt="微信公众号二维码"
							width="128"
							height="128"
						></img>
					</>,
				);
			},
		},
		{
			backgroundColor: "rgba(225, 70, 70, .1)",
			color: "rgb(225, 70, 70)",
			icon: "\ue883",
			name: t("weibo"),
			onClick: (): void => {
				open("https://weibo.com/limestart");
			},
		},
		{
			backgroundColor: "rgba(70, 150, 225, .1)",
			color: "rgb(70, 150, 225)",
			icon: "\ue611",
			name: t("bilibili"),
			onClick: (): void => {
				open("https://space.bilibili.com/14193369");
			},
		},
	];

	const feedbackChannelItems = feedbackChannelList.map((item, index) => (
		<button
			className={styles["feedback-channel-button"]}
			key={index}
			onClick={item.onClick}
			type="button"
		>
			<div
				className={styles["feedback-channel-icon"]}
				style={{
					backgroundColor: item.backgroundColor,
					color: item.color,
				}}
			>
				<i>{item.icon}</i>
			</div>
			<div className={styles["feedback-channel-name"]}>{item.name}</div>
		</button>
	));

	const faqItems = data?.map((item, index) => (
		<div
			className={styles["faq-item"]}
			key={index}
		>
			<div className={styles["faq-item-question"]}>{item.question}</div>
			<div
				className={styles["faq-item-answer"]}
				dangerouslySetInnerHTML={{ __html: item.answer }}
			></div>
		</div>
	));

	return (
		<>
			<PopupShell
				isShown={isShown}
				onClose={closeFeedbackPopup}
				title={t("feedback")}
			>
				<div className={styles["group-title"]}>
					{t("feedbackChannelsGroupTitle")}
					{i18next.language === "zh-CN" && (
						<span>
							<del>性感</del>作者在线营业
						</span>
					)}
				</div>
				<div className={styles["feedback-channel-container"]}>
					{feedbackChannelItems}
				</div>
				<div className={styles["group-title"]}>
					{t("faq")}
					<span>{t("veryLikelyToBeHelpful")}</span>
				</div>
				{faqItems}
				{isError && (
					<div className={styles["error-info"]}>
						{t("failedToLoad")}
					</div>
				)}
				<div className={styles["footer"]}>
					<FontAwesomeIcon icon={faCircleQuestion} />
					<div>
						<Trans
							components={[
								<a
									className="link"
									href={getSupportLink()}
									key="Retinbox Support"
									rel="noreferrer"
									target="_blank"
								>
									Retinbox Support
								</a>,
							]}
							i18nKey="needFurtherHelp"
						/>
					</div>
				</div>
			</PopupShell>
			<MenuShell
				className={styles["feedback-channel-detail-popup"]}
				isClosingMenu={isChannelDetailPopupClosing}
				isModal={true}
				isTransformOriginDeclared={true}
				onClose={handleChannelDetailPopupClose}
				show={isChannelDetailPopupOpen}
				x={channelDetailPopupPosition.left}
				y={channelDetailPopupPosition.top}
			>
				{channelDetailPopupContent}
			</MenuShell>
		</>
	);
}

export default FeedbackPopup;
